import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/ui/card';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Textarea } from '../../../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../../../components/ui/dialog';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { Switch } from '../../../components/ui/switch';
import { 
  Package, 
  Plus, 
  Edit2, 
  Trash2, 
  Save, 
  RefreshCw, 
  Image as ImageIcon,
  Star,
  X,
  ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import { 
  getCatalogueItems, 
  createCatalogueItem, 
  updateCatalogueItem, 
  deleteCatalogueItem,
  uploadImage 
} from '../../../services/api';

const CATEGORIES = [
  { value: 'bibles', label: 'Biblar' },
  { value: 'rosaries', label: 'Rosenkransar' },
  { value: 'books', label: 'Böcker' },
  { value: 'icons', label: 'Ikoner' },
  { value: 'other', label: 'Övrigt' },
];

const CatalogueManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [filterCategory, setFilterCategory] = useState('all');
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    name: { sv: '', en: '' },
    description: { sv: '', en: '' },
    category: 'other',
    price: '',
    images: [],
    in_stock: true,
    featured: false,
    order: 0,
    active: true,
  });

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getCatalogueItems({ active_only: false });
      setItems(data);
    } catch (error) {
      console.error('Error fetching catalogue items:', error);
      toast.error('Kunde inte hämta katalogartiklar');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const resetForm = () => {
    setFormData({
      code: '',
      name: { sv: '', en: '' },
      description: { sv: '', en: '' },
      category: 'other',
      price: '',
      images: [],
      in_stock: true,
      featured: false,
      order: 0,
      active: true,
    });
    setEditingItem(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setShowDialog(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      code: item.code || '',
      name: item.name || { sv: '', en: '' },
      description: item.description || { sv: '', en: '' },
      category: item.category || 'other',
      price: item.price !== null && item.price !== undefined ? String(item.price) : '',
      images: item.images || [],
      in_stock: item.in_stock !== false,
      featured: item.featured || false,
      order: item.order || 0,
      active: item.active !== false,
    });
    setShowDialog(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    try {
      const result = await uploadImage(file);
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, result.url]
      }));
      toast.success('Bild uppladdad!');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Kunde inte ladda upp bild');
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleSave = async () => {
    if (!formData.code.trim()) {
      toast.error('Artikelkod krävs');
      return;
    }
    if (!formData.name.sv.trim()) {
      toast.error('Svenskt namn krävs');
      return;
    }

    setSaving(true);
    try {
      const dataToSave = {
        ...formData,
        price: formData.price ? parseFloat(formData.price) : null,
      };

      if (editingItem) {
        await updateCatalogueItem(editingItem.id, dataToSave);
        toast.success('Artikel uppdaterad!');
      } else {
        await createCatalogueItem(dataToSave);
        toast.success('Artikel skapad!');
      }

      setShowDialog(false);
      resetForm();
      fetchItems();
    } catch (error) {
      console.error('Error saving item:', error);
      toast.error(error.response?.data?.detail || 'Kunde inte spara artikel');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (item) => {
    if (!window.confirm(`Ta bort "${item.name?.sv || item.code}"?`)) return;

    try {
      await deleteCatalogueItem(item.id);
      toast.success('Artikel borttagen!');
      fetchItems();
    } catch (error) {
      console.error('Error deleting item:', error);
      toast.error('Kunde inte ta bort artikel');
    }
  };

  const filteredItems = filterCategory === 'all' 
    ? items 
    : items.filter(item => item.category === filterCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Katalog</h2>
          <p className="text-gray-600">Hantera biblar, rosenkransar, böcker och ikoner</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Filtrera" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alla kategorier</SelectItem>
              {CATEGORIES.map(cat => (
                <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button onClick={openCreateDialog} className="bg-amber-600 hover:bg-amber-700">
            <Plus className="w-4 h-4 mr-2" />
            Ny artikel
          </Button>
        </div>
      </div>

      {/* Items Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredItems.map(item => (
          <Card key={item.id} className={`overflow-hidden ${!item.active ? 'opacity-60' : ''}`}>
            <div className="relative h-40 bg-gray-100">
              {item.images && item.images.length > 0 ? (
                <img 
                  src={item.images[0]} 
                  alt={item.name?.sv || item.code}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="flex items-center justify-center h-full">
                  <Package className="w-12 h-12 text-gray-300" />
                </div>
              )}
              {item.featured && (
                <div className="absolute top-2 right-2 bg-yellow-500 text-white p-1 rounded-full">
                  <Star className="w-4 h-4" />
                </div>
              )}
              {!item.active && (
                <div className="absolute top-2 left-2 bg-red-500 text-white text-xs px-2 py-1 rounded">
                  Inaktiv
                </div>
              )}
            </div>
            <CardContent className="p-4">
              <div className="flex items-start justify-between mb-2">
                <div>
                  <p className="text-xs text-gray-500 font-mono">{item.code}</p>
                  <h3 className="font-semibold text-gray-900">{item.name?.sv || 'Namnlös'}</h3>
                </div>
                {item.price && (
                  <span className="font-bold text-amber-600">{item.price} kr</span>
                )}
              </div>
              <p className="text-sm text-gray-500 mb-3 line-clamp-2">
                {item.description?.sv || 'Ingen beskrivning'}
              </p>
              <div className="flex items-center justify-between">
                <span className="text-xs px-2 py-1 bg-gray-100 rounded">
                  {CATEGORIES.find(c => c.value === item.category)?.label || item.category}
                </span>
                <div className="flex items-center gap-2">
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    onClick={() => openEditDialog(item)}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-red-600 hover:text-red-700"
                    onClick={() => handleDelete(item)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredItems.length === 0 && (
        <Card className="p-12 text-center">
          <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">Inga artiklar i katalogen ännu</p>
          <Button onClick={openCreateDialog} className="mt-4">
            <Plus className="w-4 h-4 mr-2" />
            Lägg till första artikeln
          </Button>
        </Card>
      )}

      {/* Preview Link */}
      <div className="flex justify-center">
        <a
          href="/forsamling/katalog"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline flex items-center gap-1 text-sm"
        >
          <ExternalLink className="w-4 h-4" />
          Förhandsgranska katalogen
        </a>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingItem ? 'Redigera artikel' : 'Ny artikel'}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Code & Category */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Artikelkod *
                </label>
                <Input
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="t.ex. ROS-001"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Kategori
                </label>
                <Select 
                  value={formData.category} 
                  onValueChange={(value) => setFormData({ ...formData, category: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Name */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Namn (Svenska) *
                </label>
                <Input
                  value={formData.name.sv}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    name: { ...formData.name, sv: e.target.value } 
                  })}
                  placeholder="Artikelnamn"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Namn (English)
                </label>
                <Input
                  value={formData.name.en}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    name: { ...formData.name, en: e.target.value } 
                  })}
                  placeholder="Item name"
                />
              </div>
            </div>

            {/* Description */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivning (Svenska)
                </label>
                <Textarea
                  value={formData.description.sv}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    description: { ...formData.description, sv: e.target.value } 
                  })}
                  placeholder="Artikelbeskrivning"
                  rows={3}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Beskrivning (English)
                </label>
                <Textarea
                  value={formData.description.en}
                  onChange={(e) => setFormData({ 
                    ...formData, 
                    description: { ...formData.description, en: e.target.value } 
                  })}
                  placeholder="Item description"
                  rows={3}
                />
              </div>
            </div>

            {/* Price & Order */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Pris (SEK)
                </label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="Lämna tom om inget pris"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Sorteringsordning
                </label>
                <Input
                  type="number"
                  value={formData.order}
                  onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                />
              </div>
            </div>

            {/* Images */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Bilder
              </label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.images.map((url, idx) => (
                  <div key={`image-${url}-${idx}`} className="relative w-20 h-20">
                    <img 
                      src={url} 
                      alt={`Bild ${idx + 1}`}
                      className="w-full h-full object-cover rounded border"
                    />
                    <button
                      onClick={() => removeImage(idx)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
              <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2 border border-dashed border-gray-300 rounded-lg hover:bg-gray-50">
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageUpload}
                  className="hidden"
                  disabled={uploadingImage}
                />
                {uploadingImage ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <ImageIcon className="w-4 h-4" />
                )}
                <span className="text-sm text-gray-600">
                  {uploadingImage ? 'Laddar upp...' : 'Lägg till bild'}
                </span>
              </label>
            </div>

            {/* Switches */}
            <div className="flex flex-wrap gap-6">
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.in_stock}
                  onCheckedChange={(checked) => setFormData({ ...formData, in_stock: checked })}
                />
                <span className="text-sm text-gray-700">I lager</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                />
                <span className="text-sm text-gray-700">Utvald (visas på startsidan)</span>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={formData.active}
                  onCheckedChange={(checked) => setFormData({ ...formData, active: checked })}
                />
                <span className="text-sm text-gray-700">Aktiv</span>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>
              Avbryt
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Sparar...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  {editingItem ? 'Uppdatera' : 'Skapa'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CatalogueManager;
